import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Crown, Couch, Lightning, ArrowRight, Users, GridFour,
} from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import WCStrategyNav from "../components/WCStrategyNav";
import WCCaptainChainAI from "../components/WCCaptainChainAI";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VALID_TABS = new Set(["captain-chain", "ai-analyzer", "bench-safety", "crowd-captaincy", "captain-heatmap"]);

function fmtKick(iso) {
    if (!iso) return "TBD";
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        weekday: "short", day: "numeric", month: "short",
        hour: "2-digit", minute: "2-digit",
    });
}

// ---- Captain Chain Tab ------------------------------------------------------

function CaptainChainTab() {
    const [matchday, setMatchday] = useState(1);
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [matchCount, setMatchCount] = useState(0);

    useEffect(() => {
        setLoading(true);
        axios.get(`${API}/worldcup/captain-chain?matchday=${matchday}&limit=12`)
            .then((r) => {
                setSlots(r.data.slots || []);
                setMatchCount(r.data.match_count || 0);
            })
            .catch(() => toast.error("Captain chain unavailable"))
            .finally(() => setLoading(false));
    }, [matchday]);

    return (
        <div className="space-y-3" data-testid="wc-captain-chain">
            <div className="flex items-center justify-between gap-3">
                <p className="font-mono text-[11px] text-white/60 leading-snug">
                    Premium picks sorted by kickoff time. Start with{" "}
                    <span className="text-fa-yellow font-bold">PRIMARY CAPTAIN</span> — if they blank, manually switch to the next fallback who hasn&apos;t played yet.
                </p>
                <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">MD</span>
                    {[1, 2, 3].map((m) => (
                        <button
                            key={m}
                            type="button"
                            onClick={() => setMatchday(m)}
                            data-testid={`md-toggle-${m}`}
                            className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border ${
                                matchday === m
                                    ? "bg-fa-yellow text-black border-fa-yellow"
                                    : "border-white/15 text-white/60 hover:text-white"
                            }`}
                        >
                            {m}
                        </button>
                    ))}
                </div>
            </div>
            {loading ? (
                <div className="font-mono text-[12px] text-white/40">Loading chain…</div>
            ) : slots.length === 0 ? (
                <div className="font-mono text-[12px] text-white/40">No premium picks found for MD{matchday}.</div>
            ) : (
                <div className="border border-white/10 bg-[#0d0d0d]" data-testid="captain-chain-list">
                    {slots.map((s, i) => (
                        <div
                            key={i}
                            className={`flex items-center justify-between gap-3 p-3 ${i > 0 ? "border-t border-white/5" : ""}`}
                            data-testid={`chain-slot-${s.slot_order}`}
                        >
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                <span className={`font-display font-black text-lg tabular-nums w-7 text-center ${
                                    s.slot_order === 1 ? "text-fa-yellow" : "text-white/30"
                                }`}>
                                    {s.slot_order}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-black text-sm">
                                        {s.name}{" "}
                                        <span className="text-white/40 font-mono text-[10px]">({s.team_short})</span>
                                    </div>
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                        {s.role} · {fmtKick(s.utc_date)} vs {s.opponent}
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-fa-mint">
                                    PL #{s.pl_rank} · {s.ppm?.toFixed(1)} ppm
                                </div>
                                <div className="font-mono text-[9px] text-white/30">
                                    {s.fpl_team_name} · {s.position}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <div className="font-mono text-[10px] text-white/30">
                {matchCount} matches scheduled · primary captain = earliest kickoff PL premium
            </div>
        </div>
    );
}

// ---- Bench Safety Tab -------------------------------------------------------

function BenchSafetyTab() {
    const [matchday, setMatchday] = useState(1);
    const [picks, setPicks] = useState([]);
    const [medianKickoff, setMedianKickoff] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        axios.get(`${API}/worldcup/bench-safety?matchday=${matchday}&limit=15`)
            .then((r) => {
                setPicks(r.data.picks || []);
                setMedianKickoff(r.data.median_kickoff);
            })
            .catch(() => toast.error("Bench safety unavailable"))
            .finally(() => setLoading(false));
    }, [matchday]);

    return (
        <div className="space-y-3" data-testid="wc-bench-safety">
            <div className="flex items-center justify-between gap-3">
                <p className="font-mono text-[11px] text-white/60 leading-snug">
                    Premium picks whose match kicks off <strong>after</strong> the median MD kickoff time. If an earlier starter blanks, manually sub one of these in before kickoff to recover the day.
                </p>
                <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">MD</span>
                    {[1, 2, 3].map((m) => (
                        <button
                            key={m}
                            type="button"
                            onClick={() => setMatchday(m)}
                            data-testid={`bs-md-${m}`}
                            className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border ${
                                matchday === m
                                    ? "bg-fa-mint text-black border-fa-mint"
                                    : "border-white/15 text-white/60 hover:text-white"
                            }`}
                        >
                            {m}
                        </button>
                    ))}
                </div>
            </div>
            {medianKickoff && (
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    Median MD{matchday} kickoff: {fmtKick(medianKickoff)} — only picks after this shown
                </div>
            )}
            {loading ? (
                <div className="font-mono text-[12px] text-white/40">Loading bench safety…</div>
            ) : picks.length === 0 ? (
                <div className="font-mono text-[12px] text-white/40">No bench-safety picks for MD{matchday}.</div>
            ) : (
                <div className="border border-fa-mint/30 bg-[#0d0d0d]" data-testid="bench-safety-list">
                    {picks.map((p, i) => (
                        <div
                            key={i}
                            className={`flex items-center justify-between gap-3 p-3 ${i > 0 ? "border-t border-white/5" : ""}`}
                            data-testid={`bs-pick-${p.team_id}-${p.name}`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-black text-sm">
                                    {p.name}{" "}
                                    <span className="text-white/40 font-mono text-[10px]">({p.team_short})</span>
                                </div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                    {fmtKick(p.utc_date)} vs {p.opponent} · {p.position}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-fa-mint">
                                    PL #{p.pl_rank} · {p.ppm?.toFixed(1)} ppm
                                </div>
                                <div className="font-mono text-[9px] text-white/30">
                                    {p.fpl_team_name}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ---- Crowd Captaincy Tab ----------------------------------------------------

function CrowdCaptaincyTab() {
    const [matches, setMatches] = useState([]);
    const [selectedMatchId, setSelectedMatchId] = useState(null);
    const [data, setData] = useState(null);
    const [loadingMatches, setLoadingMatches] = useState(true);
    const [loadingData, setLoadingData] = useState(false);

    useEffect(() => {
        axios.get(`${API}/worldcup/match-call/upcoming?limit=30`)
            .then((r) => {
                const m = (r.data.matches || []).filter((mm) => mm.challenge_exists);
                setMatches(m);
                if (m.length > 0) setSelectedMatchId(m[0].fd_match_id);
            })
            .catch(() => toast.error("Match list unavailable"))
            .finally(() => setLoadingMatches(false));
    }, []);

    useEffect(() => {
        if (!selectedMatchId) return;
        setLoadingData(true);
        axios.get(`${API}/worldcup/crowd-captaincy?match_id=${selectedMatchId}`)
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoadingData(false));
    }, [selectedMatchId]);

    if (loadingMatches) return <div className="font-mono text-[12px] text-white/40">Loading…</div>;
    if (matches.length === 0) {
        return (
            <div className="font-mono text-[11px] text-white/40 italic">
                No Match Call challenges yet. Generate at least one on the Match Calls tab → wait for community submissions → return here.
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="wc-crowd-captaincy">
            <p className="font-mono text-[11px] text-white/60 leading-snug">
                Each card shows <strong>% of community attempts that picked BUY + confidence ≥ 80</strong> on this player. Public hype-meter → if 70 % captain Haaland, going contrarian on van Hecke can pay 1.5–2× on a correct call.
            </p>
            {/* Match selector */}
            <div className="border border-white/10 bg-[#0d0d0d] p-2 flex items-center gap-2 overflow-x-auto" data-testid="crowd-match-selector">
                {matches.map((m) => (
                    <button
                        key={m.fd_match_id}
                        type="button"
                        onClick={() => setSelectedMatchId(m.fd_match_id)}
                        data-testid={`crowd-match-${m.fd_match_id}`}
                        className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border whitespace-nowrap ${
                            selectedMatchId === m.fd_match_id
                                ? "bg-fa-violet/20 text-fa-violet border-fa-violet"
                                : "border-white/10 text-white/50 hover:text-white"
                        }`}
                    >
                        {m.home_team_name} v {m.away_team_name}
                    </button>
                ))}
            </div>

            {loadingData ? (
                <div className="font-mono text-[12px] text-white/40">Loading captaincy…</div>
            ) : !data?.available ? (
                <div className="font-mono text-[12px] text-white/40 italic">No challenge data.</div>
            ) : data.total_attempts === 0 ? (
                <div className="font-mono text-[12px] text-white/40 italic">No community submissions yet for this match.</div>
            ) : (
                <div className="border border-white/10 bg-[#0d0d0d]" data-testid="crowd-captaincy-list">
                    <div className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
                        {data.total_attempts} total community attempts
                    </div>
                    {data.players.map((p, i) => {
                        const pct = p.captain_pct;
                        const color = pct >= 50 ? "#9EE8B4" : pct >= 25 ? "#F5E29C" : "#F08080";
                        return (
                            <div
                                key={p.player_id}
                                className="flex items-center justify-between gap-3 p-3 border-b border-white/5 last:border-0"
                                data-testid={`crowd-row-${p.player_id}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-black text-sm">
                                        {p.name}{" "}
                                        <span className="font-mono text-[10px] text-white/40">({p.nation} · {p.position})</span>
                                    </div>
                                    {p.pl_value?.value_rank && (
                                        <div className="font-mono text-[9px] uppercase tracking-widest text-fa-mint">
                                            🇬🇧 PL #{p.pl_value.value_rank} · {p.pl_value.fpl_team_name}
                                        </div>
                                    )}
                                </div>
                                <div className="text-right" style={{ color }}>
                                    <div className="font-display font-black text-2xl tabular-nums" data-testid={`crowd-pct-${p.player_id}`}>
                                        {pct}%
                                    </div>
                                    <div className="font-mono text-[9px] uppercase tracking-widest opacity-60">
                                        {p.captain_count}/{data.total_attempts} captained
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ---- Page wrapper -----------------------------------------------------------

const TAG_STYLES = {
    CONTRARIAN: { bg: "bg-fa-mint/15", border: "border-fa-mint", text: "text-fa-mint", label: "CONTRARIAN" },
    CONSENSUS: { bg: "bg-fa-yellow/15", border: "border-fa-yellow", text: "text-fa-yellow", label: "CONSENSUS" },
    BALANCED: { bg: "bg-white/10", border: "border-white/30", text: "text-white/70", label: "BALANCED" },
    NO_DATA: { bg: "bg-white/5", border: "border-white/10", text: "text-white/30", label: "NO DATA" },
};

function fmtDay(iso) {
    if (!iso || iso === "TBD") return "TBD";
    const d = new Date(`${iso}T12:00:00Z`);
    return d.toLocaleDateString(undefined, {
        weekday: "short", day: "numeric", month: "short",
    });
}

// ---- Captain Heatmap Tab ----------------------------------------------------

function CaptainHeatmapTab() {
    const [mdRange, setMdRange] = useState([1, 3]);
    const [data, setData] = useState({ days: [], crowd_data_available: false });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        axios
            .get(`${API}/worldcup/captain-heatmap?matchday_start=${mdRange[0]}&matchday_end=${mdRange[1]}&slots_per_md=6`)
            .then((r) => setData(r.data || { days: [] }))
            .catch(() => toast.error("Captain heatmap unavailable"))
            .finally(() => setLoading(false));
    }, [mdRange]);

    const days = data.days || [];

    return (
        <div className="space-y-3" data-testid="wc-captain-heatmap">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="font-mono text-[11px] text-white/60 leading-snug max-w-2xl">
                    Daily Captain Chain × Crowd Captaincy combined view.{" "}
                    <span className="text-fa-mint font-bold">CONTRARIAN</span> = premium PL pick the community ignored (&lt; 20 % captaincy) — Beat-the-Crowd <span className="text-fa-mint">+30 % FAP</span> if BUY pays off.{" "}
                    <span className="text-fa-yellow font-bold">CONSENSUS</span> = bandwagon (≥ 40 %).
                </p>
                <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">MD window</span>
                    {[[1, 1], [1, 3], [2, 3], [1, 5]].map(([s, e]) => {
                        const active = mdRange[0] === s && mdRange[1] === e;
                        return (
                            <button
                                key={`${s}-${e}`}
                                type="button"
                                onClick={() => setMdRange([s, e])}
                                data-testid={`heatmap-range-${s}-${e}`}
                                className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border ${
                                    active
                                        ? "bg-fa-yellow text-black border-fa-yellow"
                                        : "border-white/15 text-white/60 hover:text-white"
                                }`}
                            >
                                MD{s}{s !== e ? `–${e}` : ""}
                            </button>
                        );
                    })}
                </div>
            </div>

            {!data.crowd_data_available && !loading && days.length > 0 && (
                <div className="border border-white/10 bg-white/[0.02] px-3 py-2 font-mono text-[10px] text-white/40" data-testid="heatmap-nodata-notice">
                    Crowd captaincy data appears once attempts come in for the WC_MATCH_CALL challenges in this window. Tags currently show NO DATA.
                </div>
            )}

            {loading ? (
                <div className="font-mono text-[12px] text-white/40">Loading heatmap…</div>
            ) : days.length === 0 ? (
                <div className="font-mono text-[12px] text-white/40">No premium picks found in this MD window.</div>
            ) : (
                <div className="grid gap-3" data-testid="heatmap-grid" style={{ gridTemplateColumns: `repeat(${Math.min(days.length, 5)}, minmax(0, 1fr))` }}>
                    {days.map((day) => (
                        <div key={day.date} className="border border-white/10 bg-[#0d0d0d]" data-testid={`heatmap-day-${day.date}`}>
                            <div className="px-3 py-2 border-b border-white/10 bg-white/[0.02]">
                                <div className="font-display font-black text-sm uppercase tracking-tight text-fa-yellow">
                                    {fmtDay(day.date)}
                                </div>
                                <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                                    MD{day.matchday} · {day.match_count} match{day.match_count === 1 ? "" : "es"}
                                </div>
                            </div>
                            <div className="divide-y divide-white/5">
                                {day.players.map((p) => {
                                    const style = TAG_STYLES[p.tag] || TAG_STYLES.NO_DATA;
                                    return (
                                        <div
                                            key={p.player_id}
                                            className={`p-2.5 ${style.bg}`}
                                            data-testid={`heatmap-slot-${day.date}-${p.player_id}`}
                                        >
                                            <div className="flex items-center justify-between gap-2 mb-1">
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-display font-black text-[13px] truncate">
                                                        {p.name}{" "}
                                                        <span className="text-white/40 font-mono text-[9px]">({p.team_short})</span>
                                                    </div>
                                                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 truncate">
                                                        vs {p.opponent} · {fmtKick(p.utc_date).split(",").pop().trim()}
                                                    </div>
                                                </div>
                                                <span className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${style.border} ${style.text} whitespace-nowrap`}>
                                                    {style.label}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between gap-2 font-mono text-[10px]">
                                                <span className="text-fa-mint">PL #{p.pl_rank} · {p.ppm?.toFixed(1)} ppm</span>
                                                <span className={`tabular-nums ${p.crowd_captaincy_pct >= 40 ? "text-fa-yellow" : p.crowd_captaincy_pct < 20 && p.crowd_captaincy_pct !== null ? "text-fa-mint" : "text-white/40"}`}>
                                                    {p.crowd_captaincy_pct === null || p.crowd_captaincy_pct === undefined ? "— %" : `${p.crowd_captaincy_pct.toFixed(0)} %`}
                                                </span>
                                            </div>
                                            <div className="font-mono text-[9px] uppercase tracking-widest text-white/30 mt-0.5">
                                                {p.role} · slot {p.slot_order}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="font-mono text-[10px] text-white/30">
                {data.match_count || 0} matches in window · {data.challenge_match_count || 0} with active Match Call challenges
            </div>
        </div>
    );
}

// ---- Page wrapper -----------------------------------------------------------

export default function WorldCupStrategy() {
    const [searchParams, setSearchParams] = useSearchParams();
    const urlTab = searchParams.get("tab");
    const initial = VALID_TABS.has(urlTab) ? urlTab : "captain-chain";
    const [tab, setTab] = useState(initial);

    useEffect(() => {
        const newUrlTab = searchParams.get("tab");
        if (VALID_TABS.has(newUrlTab) && newUrlTab !== tab) setTab(newUrlTab);
    }, [searchParams]);

    const handleTab = (key) => {
        setTab(key);
        setSearchParams({ tab: key }, { replace: true });
    };

    return (
        <AppShell>
            <section className="space-y-5">
                <Link to="/worldcup" className="font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white inline-flex items-center gap-1.5">
                    <ArrowLeft size={11} weight="bold" /> Back to WC Hub
                </Link>

                <header className="border-2 border-fa-yellow/40 bg-[#0d0d0d] p-6" data-testid="wc-strategy-header">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                        <Lightning size={11} weight="fill" />
                        WC Strategy Tools
                    </div>
                    <h1 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight mt-2">
                        Exploit the group stage<br />by playing fixtures over names
                    </h1>
                    <p className="font-mono text-[11px] text-white/60 mt-2 leading-relaxed">
                        First 3 MDs are unlimited transfers — be aggressive. Reset before R32. <ArrowRight size={10} weight="bold" className="inline mb-0.5" /> Captain chain rotates premium picks across kickoff times; bench safety prepares your fallback subs.
                    </p>
                </header>

                {/* Shared tab bar — Round Call links out to /worldcup/matchday/1 */}
                <WCStrategyNav activeKey={tab} onTabClick={handleTab} />

                {tab === "captain-chain" && <CaptainChainTab />}
                {tab === "ai-analyzer" && <WCCaptainChainAI />}
                {tab === "bench-safety" && <BenchSafetyTab />}
                {tab === "crowd-captaincy" && <CrowdCaptaincyTab />}
                {tab === "captain-heatmap" && <CaptainHeatmapTab />}
            </section>
        </AppShell>
    );
}
